// Thin DOM layer: status line, log panel, loading overlay, aria-live region
// and the dropdown panels.

function req<T extends HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (!node) throw new Error(`Missing UI element: #${id}`);
  return node as T;
}

export const dom = {
  canvas: req<HTMLCanvasElement>('viewport'),
  statusDot: req('statusDot'),
  statusText: req('statusText'),
  liveRegion: req('liveRegion'),
  modelTitle: req('modelTitle'),
  modelDims: req('modelDims'),
  modelMeta: req('modelMeta'),
  modelUnitBtn: req<HTMLButtonElement>('modelUnitBtn'),
  modelUnitMenu: req('modelUnitMenu'),
  modelUnitHint: req<HTMLButtonElement>('modelUnitHint'),
  deviceName: req('deviceName'),
  logPanel: req('logPanel'),
  logToggle: req<HTMLButtonElement>('logToggle'),
  settingsPaneTitle: req('settingsPaneTitle'),
  loadingOverlay: req('loadingOverlay'),
  loadingText: req('loadingText'),
  cancelLoadBtn: req<HTMLButtonElement>('cancelLoadBtn'),
  dropZone: req('dropZone'),
  dropCard: req('dropCard'),
  hoverTooltip: req('hoverTooltip'),
  axisDiag: req('axisDiag'),
  axisDiagToggle: req<HTMLButtonElement>('axisDiagToggle'),
  themeToggle: req<HTMLButtonElement>('themeToggle'),
  orthoToggle: req<HTMLButtonElement>('orthoToggle'),
  displayModeBtn: req<HTMLButtonElement>('displayModeBtn'),
  displayModeIcon: req('displayModeIcon'),
  displayMenu: req('displayMenu'),
  unitToggle: req('unitToggle'),
  hatchColorRow: req('hatchColorRow'),
  bgColorRow: req('bgColorRow'),
  bgCustom: req<HTMLInputElement>('bgCustom'),
  settingsBtn: req<HTMLButtonElement>('settingsBtn'),
  settingsPanel: req('settingsPanel'),
  sectionBtn: req<HTMLButtonElement>('sectionBtn'),
  sectionPanel: req('sectionPanel'),
  sectionPlaneRow: req('sectionPlaneRow'),
  sectionControlsRow: req('sectionControlsRow'),
  sectionPickBtn: req<HTMLButtonElement>('sectionPickBtn'),
  sectionXYBtn: req<HTMLButtonElement>('sectionXYBtn'),
  sectionXZBtn: req<HTMLButtonElement>('sectionXZBtn'),
  sectionYZBtn: req<HTMLButtonElement>('sectionYZBtn'),
  sectionOffsetSlider: req<HTMLInputElement>('sectionOffsetSlider'),
  sectionOffsetValue: req('sectionOffsetValue'),
  sectionFlipBtn: req<HTMLButtonElement>('sectionFlipBtn'),
  sectionOffBtn: req<HTMLButtonElement>('sectionOffBtn'),
  sectionCloseBtn: req<HTMLButtonElement>('sectionCloseBtn'),
  measureCloseBtn: req<HTMLButtonElement>('measureCloseBtn'),
  settingsCloseBtn: req<HTMLButtonElement>('settingsCloseBtn'),
  appVersion: req('appVersion'),
  iosInstallHint: req('iosInstallHint'),
  shareViewBtn: req<HTMLButtonElement>('shareViewBtn'),
  alertDialog: req<HTMLDialogElement>('alertDialog'),
  alertTitle: req('alertTitle'),
  alertMessage: req('alertMessage'),
  alertDetail: req('alertDetail'),
  alertCloseBtn: req<HTMLButtonElement>('alertCloseBtn'),
  alertCancelBtn: req<HTMLButtonElement>('alertCancelBtn'),
  alertAltBtn: req<HTMLButtonElement>('alertAltBtn'),
  checkUpdateBtn: req<HTMLButtonElement>('checkUpdateBtn'),
  hardReloadBtn: req<HTMLButtonElement>('hardReloadBtn'),
  updateStatus: req('updateStatus'),
  alertConfirmBtn: req<HTMLButtonElement>('alertConfirmBtn'),
  fileInput: req<HTMLInputElement>('fileInput'),
  fileBtn: req<HTMLButtonElement>('fileBtn'),
  installHomeBtn: req<HTMLButtonElement>('installHomeBtn'),
  recentFiles: req('recentFiles'),
  recentFilesList: req<HTMLUListElement>('recentFilesList'),
  loadBtn: req<HTMLButtonElement>('loadBtn'),
  screenshotBtn: req<HTMLButtonElement>('screenshotBtn'),
  screenshotMenuBtn: req<HTMLButtonElement>('screenshotMenuBtn'),
  screenshotMenu: req('screenshotMenu'),
  measureBtn: req<HTMLButtonElement>('measureBtn'),
  clearMeasureBtn: req<HTMLButtonElement>('clearMeasureBtn'),
  measurePanel: req('measurePanel'),
  measureStatus: req('measureStatus'),
  measureTitle: req('measureTitle'),
  measureModeRow: req('measureModeRow'),
  measureValues: req<HTMLUListElement>('measureValues'),
  measurePrimary: req<HTMLButtonElement>('measurePrimary'),
  measurePrimaryValue: req<HTMLElement>('measurePrimaryValue'),
  measureComponents: req<HTMLElement>('measureComponents'),
  measureHelp: req<HTMLElement>('measureHelp'),
  measureHelpBtn: req<HTMLButtonElement>('measureHelpBtn'),
  measureList: req('measureList'),
  measureListItems: req<HTMLUListElement>('measureListItems'),
  measureClearAllBtn: req<HTMLButtonElement>('measureClearAllBtn'),
  connectWampBtn: req<HTMLButtonElement>('connectWampBtn'),
  disconnectBtn: req<HTMLButtonElement>('disconnectBtn'),
  viewCubeHost: req('viewCubeHost'),
  viewCubeCanvas: req<HTMLCanvasElement>('viewCube'),
  viewCubeBlur: req('viewCubeBlur'),
  viewCubeHome: req<HTMLButtonElement>('viewCubeHome'),
  treeBtn: req<HTMLButtonElement>('treeBtn'),
  titleBlock: req<HTMLElement>('titleBlock'),
  modelTreeHeader: req<HTMLElement>('modelTreeHeader'),
  modelTree: req('modelTree'),
  modelTreeClose: req<HTMLButtonElement>('modelTreeClose'),
  modelTreeShowAll: req<HTMLButtonElement>('modelTreeShowAll'),
  modelTreeMeta: req('modelTreeMeta'),
  modelTreeList: req('modelTreeList'),
  modelTreeSearch: req<HTMLInputElement>('modelTreeSearch'),
  modelTreeSearchClear: req<HTMLButtonElement>('modelTreeSearchClear'),
  modelTreeEmpty: req('modelTreeEmpty'),
  explodeBtn: req<HTMLButtonElement>('explodeBtn'),
  explodePanel: req('explodePanel'),
  explodePanelDrag: req('explodePanelDrag'),
  explodeCloseBtn: req<HTMLButtonElement>('explodeCloseBtn'),
  explodeResetBtn: req<HTMLButtonElement>('explodeResetBtn'),
  explodeAxisRow: req('explodeAxisRow'),
  explodeEmpty: req('explodeEmpty'),
  explodeSlider: req<HTMLInputElement>('explodeSlider'),
  explodeValue: req('explodeValue'),
  measurePanelDrag: req('measurePanelDrag'),
  sectionPanelDrag: req('sectionPanelDrag')
};

export type StatusState = 'idle' | 'live' | 'err';

export function setStatus(state: StatusState, text: string): void {
  dom.statusDot.className = 'dot' + (state === 'live' ? ' live' : state === 'err' ? ' err' : '');
  dom.statusText.textContent = text;
  announce(text);
}

/** Announces a message to screen readers without touching the visual status. */
export function announce(text: string): void {
  // Re-setting identical text does not re-fire the live region, so clear first.
  dom.liveRegion.textContent = '';
  dom.liveRegion.textContent = text;
}

export function log(msg: string, fresh?: boolean): void {
  const line = document.createElement('div');
  if (fresh) line.className = 'new';
  line.textContent = msg;
  dom.logPanel.appendChild(line);
  while (dom.logPanel.children.length > 2000) dom.logPanel.removeChild(dom.logPanel.firstChild!);
  dom.logPanel.scrollTop = dom.logPanel.scrollHeight;
}

/** Logs an error and announces it. */
export function logError(msg: string): void {
  log(msg, true);
  setStatus('err', msg);
}

export interface AlertOptions {
  /** Heading above the message. */
  title?: string;
  /** Smaller line under it, e.g. the list of formats that do work. */
  detail?: string;
}

/**
 * Modal for failures the user must see. The status line and the log panel sit
 * inside Settings, so on their own a rejected file looks like nothing happened
 * — on a phone there is no visible feedback at all.
 */
/** A confirm owns the dialog until it is answered; see showAlert / showConfirm. */
let confirmPending = false;

export function showAlert(message: string, options: AlertOptions = {}): void {
  logError(message);
  // There is one dialog. Writing into it while a question is up would swap the
  // text under the buttons and lose the question, so this one waits in the log.
  if (confirmPending) return;
  dom.alertTitle.textContent = options.title ?? 'Cannot open this file';
  dom.alertMessage.textContent = message;
  dom.alertDetail.textContent = options.detail ?? '';
  dom.alertDetail.classList.toggle('hidden', !options.detail);

  // <dialog> is everywhere current, but never leave the user with no message.
  if (typeof dom.alertDialog.showModal !== 'function') {
    window.alert(options.detail ? `${message}\n\n${options.detail}` : message);
    return;
  }
  if (!dom.alertDialog.open) dom.alertDialog.showModal();
}

export interface ConfirmOptions extends AlertOptions {
  /** Label of the button that goes ahead; also the primary one. */
  confirmLabel?: string;
  cancelLabel?: string;
  /** A middle way, when there is one. Left out, the dialog has two buttons. */
  altLabel?: string;
}

export type ChoiceAnswer = 'confirm' | 'alt' | 'cancel';

/**
 * Same modal, two buttons: for things the viewer will do if asked twice, like
 * opening a file that is over the size the browser usually survives. Cancel
 * takes the focus, so Enter and Esc both back out.
 */
export function showConfirm(message: string, options: ConfirmOptions = {}): Promise<boolean> {
  return showChoice(message, options).then(answer => answer === 'confirm');
}

/**
 * The same dialog with an optional third way out, for when refusing and going
 * ahead are not the only sensible answers.
 */
export function showChoice(message: string, options: ConfirmOptions = {}): Promise<ChoiceAnswer> {
  log(message, true);
  // Two modals cannot share one <dialog>: showModal() throws on the second.
  if (confirmPending) return Promise.resolve('cancel');
  dom.alertTitle.textContent = options.title ?? 'Are you sure?';
  dom.alertMessage.textContent = message;
  dom.alertDetail.textContent = options.detail ?? '';
  dom.alertDetail.classList.toggle('hidden', !options.detail);

  if (typeof dom.alertDialog.showModal !== 'function') {
    const detail = options.detail ? `\n\n${options.detail}` : '';
    return Promise.resolve(window.confirm(`${message}${detail}`) ? 'confirm' : 'cancel');
  }

  dom.alertConfirmBtn.textContent = options.confirmLabel ?? 'Continue';
  dom.alertCancelBtn.textContent = options.cancelLabel ?? 'Cancel';
  dom.alertCloseBtn.classList.add('hidden');
  dom.alertConfirmBtn.classList.remove('hidden');
  dom.alertCancelBtn.classList.remove('hidden');
  if (options.altLabel) {
    dom.alertAltBtn.textContent = options.altLabel;
    dom.alertAltBtn.classList.remove('hidden');
  }
  confirmPending = true;
  dom.alertDialog.showModal();
  dom.alertCancelBtn.focus();

  return new Promise<ChoiceAnswer>(resolve => {
    const done = (answer: ChoiceAnswer): void => {
      confirmPending = false;
      dom.alertConfirmBtn.removeEventListener('click', onConfirm);
      dom.alertAltBtn.removeEventListener('click', onAlt);
      dom.alertCancelBtn.removeEventListener('click', onCancel);
      dom.alertDialog.removeEventListener('close', onClose);
      dom.alertConfirmBtn.classList.add('hidden');
      dom.alertAltBtn.classList.add('hidden');
      dom.alertCancelBtn.classList.add('hidden');
      dom.alertCloseBtn.classList.remove('hidden');
      if (dom.alertDialog.open) dom.alertDialog.close();
      resolve(answer);
    };
    const onConfirm = (): void => done('confirm');
    const onAlt = (): void => done('alt');
    const onCancel = (): void => done('cancel');
    // Esc and the backdrop both land here, and both mean no.
    const onClose = (): void => done('cancel');
    dom.alertConfirmBtn.addEventListener('click', onConfirm);
    dom.alertAltBtn.addEventListener('click', onAlt);
    dom.alertCancelBtn.addEventListener('click', onCancel);
    dom.alertDialog.addEventListener('close', onClose);
  });
}

export function initAlertDialog(): void {
  dom.alertCloseBtn.addEventListener('click', () => dom.alertDialog.close());
  // Clicking the backdrop (the dialog element itself outside its content) closes it.
  dom.alertDialog.addEventListener('click', e => {
    if (e.target === dom.alertDialog) dom.alertDialog.close();
  });
}

/**
 * `cancellable` false hides the Cancel button instead of greying it out: only
 * the STEP/IGES worker can be stopped mid-parse, and a button that cannot do
 * what it says is worse than no button.
 */
export function setLoading(show: boolean, text?: string, cancellable = true): void {
  if (text) {
    dom.loadingText.textContent = text;
    announce(text);
  }
  dom.loadingOverlay.classList.toggle('show', show);
  dom.loadingOverlay.setAttribute('aria-busy', show ? 'true' : 'false');
  dom.cancelLoadBtn.disabled = !show || !cancellable;
  dom.cancelLoadBtn.classList.toggle('hidden', !cancellable);
}

export function setDeviceName(label: string): void {
  dom.deviceName.textContent = '';
  if (!label) return;
  const strong = document.createElement('b');
  strong.textContent = label;
  dom.deviceName.appendChild(strong);
}

export function setPanelOpen(panel: HTMLElement, button: HTMLElement, open: boolean): void {
  panel.classList.toggle('hidden', !open);
  button.setAttribute('aria-expanded', open ? 'true' : 'false');
}

export function isPanelOpen(panel: HTMLElement): boolean {
  return !panel.classList.contains('hidden');
}

function clamp(v: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, v));
}

/** Drag a floating panel by its handle (switches to fixed positioning on first drag). */
export function makePanelDraggable(panel: HTMLElement, handle: HTMLElement): void {
  let dragging = false;
  let startX = 0;
  let startY = 0;
  let originLeft = 0;
  let originTop = 0;

  /**
   * A panel with `zoom` (the model tree, on a 4K display) renders its own
   * left/top multiplied by that zoom, while the pointer is in screen pixels -
   * so the number we write has to be divided by it.
   */
  const panelZoom = (): number => {
    const z = Number.parseFloat(getComputedStyle(panel).zoom);
    return Number.isFinite(z) && z > 0 ? z : 1;
  };

  const onMove = (e: PointerEvent) => {
    if (!dragging) return;
    const width = panel.offsetWidth * panelZoom();
    const height = panel.offsetHeight * panelZoom();
    const left = clamp(originLeft + (e.clientX - startX), 8, window.innerWidth - width - 8);
    const top = clamp(originTop + (e.clientY - startY), 8, window.innerHeight - height - 8);
    const zoom = panelZoom();
    panel.style.left = `${left / zoom}px`;
    panel.style.top = `${top / zoom}px`;
  };

  const onUp = (e: PointerEvent) => {
    if (!dragging) return;
    dragging = false;
    handle.classList.remove('dragging');
    try {
      handle.releasePointerCapture(e.pointerId);
    } catch {
      /* already released */
    }
  };

  handle.addEventListener('pointerdown', e => {
    if (e.button !== 0) return;
    // Buttons in the header (close, remove) must still receive their click.
    if ((e.target as HTMLElement).closest('button')) return;
    e.preventDefault();
    e.stopPropagation();

    const rect = panel.getBoundingClientRect();
    const zoom = panelZoom();
    panel.classList.add('panel-dragged');
    panel.style.left = `${rect.left / zoom}px`;
    panel.style.top = `${rect.top / zoom}px`;
    panel.style.right = 'auto';
    panel.style.bottom = 'auto';
    panel.style.transform = 'none';

    dragging = true;
    startX = e.clientX;
    startY = e.clientY;
    originLeft = rect.left;
    originTop = rect.top;
    handle.classList.add('dragging');
    handle.setPointerCapture(e.pointerId);
  });

  handle.addEventListener('pointermove', onMove);
  handle.addEventListener('pointerup', onUp);
  handle.addEventListener('pointercancel', onUp);
}

export {
  formatAngleDeg,
  formatDistance,
  formatOffset,
  formatSize,
  getLengthUnit,
  onLengthUnitChange,
  setLengthUnit
} from './format';
